/**
 * Helper class for validating input parameters.
 * Althought TS allows to specify types, on runtime it's still possible to pass wrong types.
 * This class is used to ensure that the input parameters are correct.
 */
export default class ValidateModule {
    /**
     * Ensures that the given id is a positive integer or it's string representation
     * 
     * @param id 
     * 
     * @returns 
     */
    static checkId(id: any): boolean {
        if (id == null) {
            return this.throwError('id must be specified');
        }

        var p = parseInt(id);
        if (p != id) {
            return this.throwError('id is not an integer: ' + JSON.stringify(id));
        }

        if (id <= 0) {
            return this.throwError('id [' + id + '] must be greater than 0');
        }

        return true;
    }

    /**
     * Ensures that the given ids are positive integers or their string representation
     * 
     * @param ids 
     * 
     * @returns 
     */
    static checkIds(ids: any): boolean {
        if (!Array.isArray(ids)) {
            return this.throwError('ids must be an array');
        }

        for (var i=0; i<ids.length; i++) {
            if (!this.checkId(ids[i])) {
                return false;
            }
        }

        return true;
    }

    /**
     * Ensures that the given id is a integer or it's string representation
     * 
     * @param n
     * @param disallowNegative don't allow negative numbers
     * @param disallowZero don't allow zero
     * 
     * @returns 
     */
    static checkInt(n: any, disallowNegative?: boolean, disallowZero?: boolean): boolean {
        if (n == null) {
            return this.throwError('Missing mandatory argument');
        }

        var p = parseInt(n);
        if (p != n) {
            return this.throwError('Argument is not an integer');
        }

        if (disallowNegative && p < 0) {
            return this.throwError('Argument can\'t be negative');
        }

        if (disallowZero && p == 0) {
            return this.throwError('Argument can\'t be zero');
        }

        return true;
    }

    /**
     * Ensures that the given ids are integers or their string representation
     *
     * @param ns
     * @param disallowNegative don't allow negative numbers
     * @param disallowZero don't allow zero
     *
     * @returns
     */
    static checkInts(ns: any, disallowNegative?: boolean, disallowZero?: boolean): boolean {
        if (!Array.isArray(ns)) {
            return this.throwError('Argument must be an array');
        }

        for (var i=0; i<ns.length; i++) {
            if (!this.checkInt(ns[i], disallowNegative, disallowZero)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Checks if the given argument is not null or undefined (i.e. mandatory argument) and, optionally, if it's one of the valid values (comparison done using ==)
     * 
     * @param arg 
     * @param validValues 
     * 
     * @returns 
     */
    static checkMandatoryArg(arg: any, ...validValues: any[]): boolean {
        if (arg == null) {
            return this.throwError('Missing mandatory argument');
        }
        
        if (validValues.length > 0 && !validValues.includes(arg)) {
            return this.throwError('Specified value [' + arg + '] is not an acceptable value [' + validValues + ']');
        }

        return true;
    }

    /**
     * Checks if the given argument is not null or undefined (i.e. mandatory argument) and, optionally, if it's not empty
     * 
     * @param arr 
     * @param isEmptyInvalid 
     * 
     * @returns 
     */
    static checkMandatoryArray(arr, isEmptyInvalid?: boolean): boolean {
        if (arr == null) {
            return this.throwError('Missing mandatory argument');
        }
        
        if (!Array.isArray(arr)) {
            return this.throwError('arr must be an array');
        }
        
        if (isEmptyInvalid && arr.length == 0) {
            return this.throwError('arr must have elements');
        }
        
        return true;
    }

    /**
     * Basic sanity check... id and entity must exist, id must be > 0 and id must match entity.id
     */
    static checkPutRequest(id, entity) {
        if (!es.module.validateId(id)) {
            return false;
        }

        if (entity == null) {
            return this.throwError('entity must be specified');
        }

        if (entity.id != id) {
            return this.throwError('entity id [' + entity.id + '] must match id [' + id + ']');
        }

        return true;
    }

    /**
     * Throws an error in different 'thread' and returns false
     */
    private static throwError(msg: string) {
        var e = new Error(msg);
        window.setTimeout(function() { throw e; }, 1); // Must throw Error on another 'thread' as jQuery was swallowing them inside done callback

        var jsErrorMsg = 'Internal error: ' + msg;
        if (e.stack) {
            jsErrorMsg += '\n' + e.stack;
        }

        es.error.handleJSError(jsErrorMsg);

        return false;
    }
}